import { useEffect, useState } from "react";
import {
  DeliveryService,
  type MailSend,
  type MailSendList,
} from "@services/delivery/deliveryService";

/*
    The mail a campaign has agreed to post, as a list of batches rather than one event.

    Rachel: "for direct mail there can be multiple sends that we keep track of - each shows
    the number of recipients its going to the cost and the estimated delivery - they need to
    approve and we show how much of the budget that used", and "a send can go over the
    campaign budget and they are warned and can proceed".

    WHY APPROVAL IS A STEP AND NOT A CHECKBOX. Mail is the one channel with no governor. An ad
    set with no money stops serving; postcards are priced, printed and posted in one pass, and
    the first sign of trouble is an invoice for post that has already arrived. Letting a send
    exceed the budget is only coherent if something else becomes the governor, and here that
    is a person, by name, against a figure. So an approved send records who approved it, what
    it was going to cost, and what the budget looked like at that moment -- an approval that
    cannot say what it was approving is not an approval.

    WHY THE COST DOES NOT MOVE AFTER APPROVAL. The audience can grow between agreeing and
    posting. A send that quietly costs more than the number on the approval is exactly the
    surprise this exists to prevent, so the figure is frozen and any gap is reported.
*/

interface MailSendsPanelProps {
  launchId: number;
  /** Refetch when the channel's setup changes, since the estimate moves with it. */
  configKey?: string | number | null;
}

const money = (value: string | number | null | undefined) =>
  value === null || value === undefined
    ? "—"
    : `$${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

const count = (n: number) => n.toLocaleString();

const day = (iso: string | null) =>
  iso
    ? new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : null;

/** Today in the browser's own timezone, for a date input's floor. */
const today = () => {
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().slice(0, 10);
};

const STATE_STYLE: Record<string, string> = {
  draft: "bg-secondary-subtle text-secondary-emphasis",
  awaiting_approval: "bg-warning-subtle text-warning-emphasis",
  approved: "bg-success-subtle text-success-emphasis",
  sending: "bg-info-subtle text-info-emphasis",
  sent: "bg-primary-subtle text-primary-emphasis",
  canceled: "bg-light text-muted",
};

export default function MailSendsPanel({ launchId, configKey }: MailSendsPanelProps) {
  const [data, setData] = useState<MailSendList | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** The send whose over-budget warning is open, and the message the server sent with it. */
  const [overBudget, setOverBudget] = useState<{ send: MailSend; message: string } | null>(null);
  const [newDate, setNewDate] = useState<string>("");
  const [newName, setNewName] = useState<string>("");

  const service = new DeliveryService();

  const load = async () => {
    try {
      setData(await service.listSends(launchId));
    } catch {
      setError("Could not load the mail sends for this campaign.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    service
      .listSends(launchId)
      .then((rows) => !cancelled && setData(rows))
      .catch(() => !cancelled && setError("Could not load the mail sends for this campaign."))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [launchId, configKey]);

  const prepare = async () => {
    setBusy(true);
    setError(null);
    try {
      await service.prepareSend(launchId, {
        name: newName || undefined,
        scheduled_date: newDate || null,
      });
      setNewName("");
      setNewDate("");
      await load();
    } catch (e) {
      // The server refuses a send with nobody to mail, or an unfinished setup, and says
      // which. That message beats anything this component could guess at.
      setError(e instanceof Error ? e.message : "Could not cost this send.");
    } finally {
      setBusy(false);
    }
  };

  const approve = async (send: MailSend, accept = false) => {
    setBusy(true);
    setError(null);
    try {
      await service.approveSend(launchId, send.id, accept);
      setOverBudget(null);
      await load();
    } catch (e) {
      const message = e instanceof Error ? e.message : "Could not approve this send.";
      // 409 is the budget refusal specifically, and it is not a failure: it is the warning,
      // and the send can still go ahead if somebody says so on purpose.
      if ((e as { status?: number })?.status === 409) {
        setOverBudget({ send, message });
      } else {
        setError(message);
      }
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (send: MailSend) => {
    setBusy(true);
    setError(null);
    try {
      await service.cancelSend(launchId, send.id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not cancel this send.");
    } finally {
      setBusy(false);
    }
  };

  const reschedule = async (send: MailSend, date: string) => {
    setBusy(true);
    setError(null);
    try {
      await service.updateSend(launchId, send.id, { scheduled_date: date || null });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not change the date.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return null;

  const sends = data?.sends ?? [];
  const remaining = data?.budget.remaining ?? null;
  const spent = data?.budget.spent_on_mail ?? "0";
  const capped = remaining !== null;

  return (
    <div className="mt-3 pt-3 border-top">
      <div className="d-flex align-items-center justify-content-between mb-2 gap-2 flex-wrap">
        <div>
          <div className="fw-semibold">Sends</div>
          <div className="text-muted small">
            Each batch is costed and approved before anything is printed.
          </div>
        </div>
        <div className="text-end small">
          <div>
            <span className="text-muted">Spent on mail </span>
            <span className="fw-semibold">{money(spent)}</span>
          </div>
          {/* No contract means no ceiling, and inventing one would train people to click
              through a warning that never meant anything. */}
          <div className="text-muted">
            {capped ? `${money(remaining)} left in the budget` : "No budget set for this engagement"}
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2 small">{error}</div>}

      {overBudget && (
        <div className="alert alert-warning py-2 small">
          <div className="fw-semibold mb-1">This send goes over the budget</div>
          <div className="mb-2">{overBudget.message}</div>
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-sm btn-warning"
              disabled={busy}
              onClick={() => approve(overBudget.send, true)}
            >
              Approve anyway
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              disabled={busy}
              onClick={() => setOverBudget(null)}
            >
              Leave it
            </button>
          </div>
        </div>
      )}

      {sends.length === 0 ? (
        <div className="text-muted small mb-3">
          No sends yet. Costing one tells you how many people it reaches and what it comes to,
          without committing to it.
        </div>
      ) : (
        <div className="table-responsive mb-3">
          <table className="table table-sm align-middle mb-0">
            <thead>
              <tr className="text-muted small">
                <th>Send</th>
                <th className="text-end">Recipients</th>
                <th className="text-end">Cost</th>
                <th>Arrives about</th>
                <th>Approval</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sends.map((send) => (
                <tr key={send.id}>
                  <td>
                    <div className="fw-semibold small">{send.name || "Mail send"}</div>
                    <span
                      className={`badge ${STATE_STYLE[send.state] ?? "bg-light text-muted"}`}
                    >
                      {send.state_label}
                    </span>
                    {send.approved_over_budget && (
                      <span className="badge bg-warning-subtle text-warning-emphasis ms-1">
                        over budget by {money(send.over_budget_by)}
                      </span>
                    )}
                  </td>
                  <td className="text-end">
                    {count(send.actual_pieces || send.estimated_pieces)}
                  </td>
                  <td className="text-end">
                    {money(send.state === "sent" ? send.actual_cost : send.estimated_cost)}
                    {/* The approved figure is not overwritten by what it came to. Somebody
                        agreed to a number, and replacing it would erase what the approval
                        was about. */}
                    {send.state === "sent" &&
                      Number(send.actual_cost) !== Number(send.estimated_cost) && (
                        <div className="text-muted" style={{ fontSize: 11 }}>
                          approved {money(send.estimated_cost)}
                        </div>
                      )}
                  </td>
                  <td className="small">
                    {send.is_open ? (
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        style={{ maxWidth: 160 }}
                        min={today()}
                        value={send.scheduled_date ?? ""}
                        disabled={busy}
                        onChange={(e) => reschedule(send, e.target.value)}
                        aria-label={`Send date for ${send.name || "mail send"}`}
                      />
                    ) : (
                      day(send.estimated_delivery) ?? "—"
                    )}
                    {send.is_open && (
                      <div className="text-muted" style={{ fontSize: 11 }}>
                        {send.scheduled_date
                          ? `arrives about ${day(send.estimated_delivery)}`
                          : "goes with the next publish"}
                      </div>
                    )}
                  </td>
                  <td className="small">
                    {send.approved_by_name ? (
                      <>
                        <div>{send.approved_by_name}</div>
                        <div className="text-muted" style={{ fontSize: 11 }}>
                          {send.approved_at
                            ? new Date(send.approved_at).toLocaleDateString()
                            : ""}
                          {send.budget_remaining_at_approval !== null &&
                            ` · ${money(send.budget_remaining_at_approval)} left then`}
                        </div>
                      </>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td className="text-end">
                    {send.state === "awaiting_approval" && (
                      <button
                        type="button"
                        className="btn btn-sm btn-primary me-1"
                        disabled={busy}
                        onClick={() => approve(send)}
                      >
                        Approve
                      </button>
                    )}
                    {send.is_open && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        disabled={busy}
                        onClick={() => cancel(send)}
                      >
                        Cancel
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="d-flex gap-2 align-items-end flex-wrap">
        <div style={{ minWidth: 180, flex: "1 1 auto" }}>
          <label className="form-label small text-muted mb-1" htmlFor="new-send-name">
            New send
          </label>
          <input
            id="new-send-name"
            className="form-control form-control-sm"
            placeholder="October drop"
            value={newName}
            disabled={busy}
            onChange={(e) => setNewName(e.target.value)}
          />
        </div>
        <div style={{ minWidth: 150 }}>
          <label className="form-label small text-muted mb-1" htmlFor="new-send-date">
            Send on
          </label>
          <input
            id="new-send-date"
            type="date"
            className="form-control form-control-sm"
            min={today()}
            value={newDate}
            disabled={busy}
            onChange={(e) => setNewDate(e.target.value)}
          />
        </div>
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          disabled={busy}
          onClick={prepare}
        >
          Cost this send
        </button>
      </div>
      <div className="form-text">
        Costing a send does not commit to it. Leave the date empty to go with the next publish.
      </div>
    </div>
  );
}
